import { useState, type RefObject } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export type SignatureTab = "drawn" | "typed" | "certificate";

interface SignatureTabsProps {
  tab: SignatureTab;
  onTabChange: (tab: SignatureTab) => void;
  signerName: string;
  typedName: string;
  onTypedNameChange: (v: string) => void;
  certFile: File | null;
  onCertFileChange: (f: File | null) => void;
  certPwd: string;
  onCertPwdChange: (v: string) => void;
  sigPadRef: RefObject<SignatureCanvas>;
  canvasHeight?: number;
  certHint?: string;
}

const triggerClass =
  "rounded-[9px] px-4 py-[9px] text-[12.5px] font-semibold text-muted-foreground data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm";

export function SignatureTabs({
  tab,
  onTabChange,
  signerName,
  typedName,
  onTypedNameChange,
  certFile,
  onCertFileChange,
  certPwd,
  onCertPwdChange,
  sigPadRef,
  canvasHeight = 170,
  certHint = "O arquivo não sai do seu navegador",
}: SignatureTabsProps) {
  const [hasStroke, setHasStroke] = useState(false);

  return (
    <Tabs value={tab} onValueChange={(v) => onTabChange(v as SignatureTab)}>
      <TabsList className="inline-flex h-auto w-fit flex-wrap gap-[3px] rounded-[11px] bg-background p-[3px]">
        <TabsTrigger value="drawn" className={triggerClass}>
          Desenhar
        </TabsTrigger>
        <TabsTrigger value="typed" className={triggerClass}>
          Digitar
        </TabsTrigger>
        <TabsTrigger value="certificate" className={triggerClass}>
          Certificado A1
        </TabsTrigger>
      </TabsList>

      <TabsContent value="drawn" className="mt-[18px]">
        <div
          className="relative overflow-hidden rounded-[14px] border border-dashed border-border bg-background"
          style={{ height: canvasHeight }}
        >
          <SignatureCanvas
            ref={sigPadRef}
            canvasProps={{ className: "w-full h-full" }}
            penColor="#111"
            onBegin={() => setHasStroke(true)}
          />
          {!hasStroke && (
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-[12.5px] text-muted-foreground">
              Desenhe sua assinatura aqui com o dedo ou o mouse
            </span>
          )}
        </div>
        <div className="mt-2.5 flex justify-end">
          <button
            type="button"
            className="text-xs font-semibold text-muted-foreground hover:text-foreground"
            onClick={() => {
              sigPadRef.current?.clear();
              setHasStroke(false);
            }}
          >
            Limpar
          </button>
        </div>
      </TabsContent>

      <TabsContent value="typed" className="mt-[18px] flex flex-col gap-2">
        <Label
          htmlFor="signature-typed-name"
          className="text-[12.5px] font-bold text-muted-foreground"
        >
          Digite seu nome completo
        </Label>
        <Input
          id="signature-typed-name"
          value={typedName}
          onChange={(e) => onTypedNameChange(e.target.value)}
          placeholder={signerName}
          className="h-16 rounded-xl border-border bg-background px-[18px] font-display text-[26px] placeholder:text-muted-foreground/60"
        />
        <span className="text-[11.5px] text-muted-foreground">
          Precisa corresponder exatamente ao nome do signatário.
        </span>
      </TabsContent>

      <TabsContent value="certificate" className="mt-[18px] flex flex-col gap-3">
        <label className="flex h-24 cursor-pointer flex-col items-center justify-center gap-1.5 rounded-[14px] border border-dashed border-border bg-background text-center transition-colors hover:border-primary">
          <input
            type="file"
            accept=".p12,.pfx"
            className="sr-only"
            onChange={(e) => onCertFileChange(e.target.files?.[0] || null)}
          />
          <span className="text-[13px] font-semibold text-foreground">
            {certFile ? certFile.name : "Selecione seu certificado .p12 ou .pfx"}
          </span>
          <span className="text-[11.5px] text-muted-foreground">{certHint}</span>
        </label>
        <div className="flex flex-col gap-[7px]">
          <Label
            htmlFor="signature-cert-password"
            className="text-[12.5px] font-bold text-muted-foreground"
          >
            Senha do certificado
          </Label>
          <Input
            id="signature-cert-password"
            type="password"
            value={certPwd}
            onChange={(e) => onCertPwdChange(e.target.value)}
            className="h-11 rounded-[11px] border-border bg-background px-3.5"
          />
        </div>
      </TabsContent>
    </Tabs>
  );
}
